import React, { useEffect, useRef } from 'react';
import { View, Platform } from 'react-native';
import styled from 'styled-components/native';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
    ScrollView,
    Text,
    Animated,
    Linking,
    TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { StackNavigationProp } from '@react-navigation/stack';
import MenuCard from '../components/MenuCard';
import { theme } from '../theme';
import { DefaultTheme } from 'styled-components';
import { RootStackParamList } from '../types';
import Logo from '../components/Logo';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

type HomeScreenNavigationProp = StackNavigationProp<RootStackParamList, 'Home'>;

interface HomeScreenProps {
    navigation: HomeScreenNavigationProp;
}

type ThemeProps = { theme: DefaultTheme };

const Container = styled(SafeAreaView)`
    flex: 1;
    background-color: #0a0e27;
`;

const ScrollContainer = styled(ScrollView)`
    flex: 1;
`;

const Content = styled.View`
    padding: ${(props: ThemeProps) => props.theme.spacing.lg}px;
    padding-bottom: ${(props: ThemeProps) => props.theme.spacing.xxl}px;
    max-width: 800px;
    align-self: center;
    width: 100%;
`;

const Footer = styled.View`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: ${(props: ThemeProps) => props.theme.spacing.md}px;
    align-items: center;
    background-color: rgba(10, 14, 39, 0.9);
    border-top-width: 1px;
    border-top-color: rgba(255, 255, 255, 0.1);
`;

const Header = styled.View`
    margin-bottom: ${(props: ThemeProps) => props.theme.spacing.xl}px;
    margin-top: ${(props: ThemeProps) => props.theme.spacing.lg}px;
    align-items: center;
    background-color: transparent;
`;

const Title = styled(Text)`
    color: ${(props: ThemeProps) => props.theme.colors.text};
    font-size: 42px;
    font-weight: 800;
    margin-bottom: ${(props: ThemeProps) => props.theme.spacing.md}px;
    letter-spacing: -0.5px;
    text-shadow: 0px 0px 30px rgba(124, 58, 237, 0.5);
`;

const Subtitle = styled(Text)`
    color: ${(props: ThemeProps) => props.theme.colors.textSecondary};
    font-size: ${(props: ThemeProps) => props.theme.typography.body.fontSize}px;
    line-height: ${(props: ThemeProps) =>
        props.theme.typography.body.lineHeight}px;
`;

const InstallButton = styled.TouchableOpacity`
    background-color: ${(props: ThemeProps) => props.theme.colors.primary};
    padding: ${(props: ThemeProps) => props.theme.spacing.md}px
        ${(props: ThemeProps) => props.theme.spacing.lg}px;
    border-radius: 12px;
    align-items: center;
    justify-content: center;
    margin-top: ${(props: ThemeProps) => props.theme.spacing.lg}px;
    flex-direction: row;
`;

const InstallButtonText = styled(Text)`
    color: white;
    font-size: ${(props: ThemeProps) => props.theme.typography.body.fontSize}px;
    font-weight: 600;
    margin-left: ${(props: ThemeProps) => props.theme.spacing.sm}px;
`;

// TitleBadge removed for a cleaner aesthetic

const HomeScreen: React.FC<HomeScreenProps> = ({ navigation }) => {
    const { t } = useTranslation();
    const { isVisible, handleInstall } = useInstallPrompt();
    const titleOpacity = useRef(new Animated.Value(0)).current;
    const titleTranslateY = useRef(new Animated.Value(20)).current;
    // pulseAnim removed for a cleaner title effect
    const subtitleOpacity = useRef(new Animated.Value(0)).current;
    const subtitleTranslateY = useRef(new Animated.Value(20)).current;

    useEffect(() => {
        Animated.parallel([
            Animated.timing(titleOpacity, {
                toValue: 1,
                duration: 350,
                delay: 120,
                useNativeDriver: true,
            }),
            Animated.spring(titleTranslateY, {
                toValue: 0,
                delay: 120,
                useNativeDriver: true,
            }),
            Animated.timing(subtitleOpacity, {
                toValue: 1,
                duration: 300,
                delay: 220,
                useNativeDriver: true,
            }),
            Animated.spring(subtitleTranslateY, {
                toValue: 0,
                delay: 220,
                useNativeDriver: true,
            }),
        ]).start();
    }, []);

    return (
        <Container>
            <ScrollContainer>
                <Content>
                    <Header>
                        <Animated.View
                            style={{
                                opacity: titleOpacity,
                                transform: [{ translateY: titleTranslateY }],
                                alignItems: 'center',
                            }}
                        >
                            <Animated.View style={{ alignItems: 'center' }}>
                                <Logo width={300} height={80} />
                            </Animated.View>
                            <Subtitle
                                style={{ marginTop: 6, textAlign: 'center' }}
                            >
                                {t('home.subtitle')}
                            </Subtitle>
                        </Animated.View>
                    </Header>

                    {Platform.OS === 'web' && isVisible && (
                        <InstallButton onPress={handleInstall}>
                            <Ionicons
                                name="download-outline"
                                size={20}
                                color="white"
                            />
                            <InstallButtonText>
                                Add to Home Screen
                            </InstallButtonText>
                        </InstallButton>
                    )}

                    <MenuCard
                        icon="book-outline"
                        title={t('home.training')}
                        description={t('home.trainingDescription')}
                        onPress={() => navigation.navigate('Training')}
                        delay={300}
                        iconColor={theme.colors.primaryLight}
                    />

                    <MenuCard
                        icon="stats-chart-outline"
                        title={t('home.statistics')}
                        description={t('home.statisticsDescription')}
                        onPress={() => navigation.navigate('Statistics')}
                        delay={400}
                        iconColor={theme.colors.secondary}
                    />

                    <MenuCard
                        icon="information-circle-outline"
                        title={t('home.info')}
                        description={t('home.infoDescription')}
                        onPress={() => navigation.navigate('Info')}
                        delay={450}
                        iconColor={theme.colors.primaryDark}
                    />

                    <MenuCard
                        icon="settings-outline"
                        title={t('home.settings')}
                        description={t('home.settingsDescription')}
                        onPress={() => navigation.navigate('Settings')}
                        delay={500}
                        iconColor={theme.colors.accent}
                    />
                </Content>
            </ScrollContainer>

            <Footer>
                <TouchableOpacity
                    onPress={() =>
                        Linking.openURL(
                            'https://www.paypal.com/paypalme/mpoulhazan'
                        )
                    }
                    style={{ flexDirection: 'row', alignItems: 'center' }}
                >
                    <Ionicons
                        name="logo-paypal"
                        size={20}
                        color={theme.colors.textSecondary}
                    />
                    <Text
                        style={{
                            color: theme.colors.textSecondary,
                            marginLeft: theme.spacing.sm,
                        }}
                    >
                        Support CanExam
                    </Text>
                </TouchableOpacity>
            </Footer>
        </Container>
    );
};

export default HomeScreen;
